/**
 * AI Configuration page — the AI Center's model-routing screen.
 *
 * Shows every configured AI provider with live health, the tenant's model
 * aliases, per-provider chat/embedding model names, default provider, and
 * fallback chain, and lets an admin edit them.
 *
 * Values shown are *effective* — the platform default with this tenant's
 * override layered on top. `config.overrides` reports which fields the tenant
 * actually customised, so each one is badged "Custom" vs "Inherited" and the
 * admin can tell what a reset would change.
 *
 * A compact Test Console sits at the bottom so a config change can be
 * verified without leaving the page; the full version — system prompt, larger
 * editor, run history — lives on the Playground page it links to.
 *
 * Visual language follows the agent workspace (components/agent/*) via the
 * shared primitives in components/admin/ai/ui.tsx.
 *
 * Backend contract:
 *   GET  /admin/ai/config   -> AiConfigResponse
 *   PUT  /admin/ai/config   -> AiConfigResponse (ADMIN_EXTENSIONS_MANAGE)
 *   GET  /admin/ai/health   -> { status, providers: ProviderHealth[] }
 *
 * Edit-drawer tab order follows the setup flow an admin actually walks
 * through: authenticate (API Keys) -> where the provider lives (Endpoints,
 * cloud providers rarely need this) -> which chat model to use per provider
 * (Models) -> which model to use for each secondary task (Task Models:
 * embedding / code / guardrail, merged into one tab since they're the same
 * "task -> provider:model" pattern) -> policy on top of all of it (Routing,
 * which now also owns Aliases, since an alias is just a named routing
 * preset).
 */
import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react'
import { Link } from 'react-router'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import {
  Boxes,
  CircleAlert,
  ChevronUp,
  ChevronDown,
  Cpu,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Route,
  Server,
  FlaskConical,
  Trash2 } from 'lucide-react'

import RequirePermission from '../../components/auth/RequirePermission.js'
import ResourceDrawer from '../../components/admin/ResourceDrawer.js'
import AiTestConsole from '../../components/admin/ai/AiTestConsole.js'
import { ModelSelect } from '../../components/admin/ai/ModelSelect.js'
import { ProviderKeyManager, type ProviderKeyInfo } from '../../components/admin/ai/ProviderKeyManager.js'
import {
  ActionButton,
  DROPDOWN_CLASS,
  DROPDOWN_PANEL_CLASS,
  Field,
  OverrideTag,
  ValueBox,
} from '../../components/admin/ai/ui.js'
import { useConfirmDialog } from '../../components/ui/ConfirmDialog.js'
import { api } from '../../api/client.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { useAuthStore } from '../../store/authStore.js'
import Select from '../../components/ui/Select.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Which fields this tenant has overridden; everything else is inherited. */
interface AiConfigOverrides {
  aliases: Record<string, string>
  providerModels: Record<string, string>
  embeddingModels: Record<string, string>
  codeModels: Record<string, string>
  guardrailModels: Record<string, string>
  providerUrls?: Record<string, string>
  customProviders?: CustomProvider[]
  defaultProvider?: string
  fallbackChain?: string[]
}

interface CustomProvider {
  name: string
  adapter: 'openai' | 'ollama' | 'anthropic' | 'google' | 'groq'
  baseUrl: string
  chatModel: string
  embeddingModel?: string | undefined
  codeModel?: string | undefined
  guardrailModel?: string | undefined
  apiKeyRef?: string
  isBuiltIn?: boolean
  displayName?: string
}

interface AiConfig {
  aliases: Record<string, string>
  /** Alias display metadata served by the API — falls back to ALIAS_LABELS. */
  aliasMeta?: Array<{ key: string; label: string }>
  defaultProvider: string
  fallbackChain: string[]
  providerUrls: Record<string, string>
  customProviders: CustomProvider[]
  providerModels: Record<string, string>
  embeddingModels: Record<string, string>
  codeModels: Record<string, string>
  guardrailModels: Record<string, string>
  ollamaUrl: string
  hasOpenAIKey: boolean
  hasGroqKey: boolean
  hasAnthropicKey: boolean
  hasGoogleKey: boolean
  overrides: AiConfigOverrides
  /** Per-provider key metadata (masked hints + source). Keyed by provider name. */
  providerKeys: Record<string, ProviderKeyInfo>
  /** Pure platform defaults — lets fields offer a "reset to default" link. */
  platform?: {
    aliases: Record<string, string>
    defaultProvider: string
    fallbackChain: string[]
    providerModels: Record<string, string>
    embeddingModels: Record<string, string>
    codeModels: Record<string, string>
    guardrailModels: Record<string, string>
    providerUrls: Record<string, string>
    customProviders: CustomProvider[]
  }
}

interface ProviderHealth {
  provider: string
  available: boolean
  latencyMs?: number
  error?: string
  /** The URL that was actually probed. */
  endpoint?: string
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Built-ins render first, in this fixed order; custom providers follow in stored order. */
const BUILT_IN_ORDER = ['ollama', 'openai', 'groq', 'anthropic', 'google']

function sortProviders(providers: CustomProvider[]): CustomProvider[] {
  return [...providers].sort((a, b) => {
    const ai = BUILT_IN_ORDER.indexOf(a.name)
    const bi = BUILT_IN_ORDER.indexOf(b.name)
    if (ai === -1 && bi === -1) return 0
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
}

/** Adapter family label shown as the row subtitle. */
const ADAPTER_LABELS: Record<CustomProvider['adapter'], string> = {
  openai: 'OpenAI-compatible',
  ollama: 'Ollama native',
  anthropic: 'Anthropic',
  google: 'Google',
  groq: 'Groq (OpenAI-compatible)',
}

/**
 * Fallback labels only — the alias list renders from `config.aliasMeta`
 * served by GET /admin/ai/config; this map covers an older API response.
 */
const ALIAS_LABELS: Record<string, string> = {
  fast_private: 'Fast / Private (local)',
  reasoning_premium: 'Reasoning / Premium',
  fast_cloud: 'Fast / Cloud',
  reasoning_claude: 'Reasoning / Claude',
  multimodal: 'Multimodal',
}

// Field styling inside the edit drawer — identical to the Guardrails / Agent
// drawers (see EditAgentDrawer's inputClass/labelClass).
const DRAWER_INPUT_CLASS =
  'w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]'
const DRAWER_LABEL_CLASS =
  'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400'
const DRAWER_SECTION_CLASS = 'mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500'
const DRAWER_SUBSECTION_CLASS =
  'mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400'

/** Common model names per provider — shown as <datalist> suggestions so users
 *  aren't expected to know valid model identifiers. Free text still works. */
const MODEL_SUGGESTIONS: Record<string, string[]> = {
  ollama: ['llama3.1:8b', 'qwen3.8:latest', 'mistral:7b', 'phi3:mini'],
  openai: ['gpt-4o-mini', 'gpt-4o', 'o3-mini'],
  groq: ['openai/gpt-oss-120b', 'openai/gpt-oss-20b', 'qwen/qwen3.8-27b'],
  anthropic: ['claude-3-5-sonnet-20241022', 'claude-3-5-haiku-20241022', 'claude-sonnet-4-20250514'],
  google: ['gemini-2.0-flash', 'gemini-1.5-flash', 'gemini-1.5-pro'],
}

const EMBEDDING_SUGGESTIONS: Record<string, string[]> = {
  ollama: ['nomic-embed-text', 'mxbai-embed-large', 'all-minilm'],
  openai: ['text-embedding-3-small', 'text-embedding-3-large', 'text-embedding-ada-002'],
  groq: ['openai/gpt-oss-20b', 'openai/gpt-oss-120b'],
  anthropic: ['claude-3-5-sonnet-20241022'],
  google: ['gemini-embedding-exp', 'text-embedding-004'],
}

const CODE_SUGGESTIONS: Record<string, string[]> = {
  ollama: ['codellama:7b', 'deepseek-coder:6.7b', 'qwen2.5-coder:7b', 'codegemma:7b'],
  openai: ['codex-mini-latest', 'gpt-4.1', 'o3-mini'],
  groq: ['openai/gpt-oss-120b', 'openai/gpt-oss-20b', 'qwen/qwen3.8-27b'],
  anthropic: ['claude-sonnet-4-20250514', 'claude-3-5-sonnet-20241022', 'claude-opus-4-20250514'],
  google: ['gemini-2.5-pro', 'gemini-2.0-flash', 'gemini-1.5-pro'],
}

const GUARDRAIL_SUGGESTIONS: Record<string, string[]> = {
  ollama: ['llama-guard3:8b', 'llama-guard2:7b', 'guardgemma:2b'],
  openai: ['gpt-4o-mini', 'gpt-4o', 'o3-mini'],
  groq: ['openai/gpt-oss-safeguard-20b', 'openai/gpt-oss-120b', 'openai/gpt-oss-20b'],
  anthropic: ['claude-3-5-haiku-20241022', 'claude-3-5-sonnet-20241022', 'claude-sonnet-4-20250514'],
  google: ['gemini-2.0-flash', 'gemini-1.5-flash', 'gemini-1.5-pro'],
}

/**
 * Small "reset to default" link shown beside a field label when the current
 * value differs from the platform default.
 */
function ResetLink({
  platformValue,
  currentValue,
  onReset,
}: {
  platformValue: string | undefined
  currentValue: string
  onReset: () => void
}): JSX.Element | null {
  if (platformValue === undefined || currentValue === platformValue) return null
  return (
    <button
      type="button"
      onClick={onReset}
      title={`Reset to platform default: ${platformValue}`}
      className="ml-1 inline-flex items-center gap-0.5 rounded-sm px-1 py-0.5 text-[10px] font-medium text-[#3454cf] transition-colors hover:bg-[#3454cf]/10"
    >
      <RotateCcw className="h-2.5 w-2.5" />
      Reset
    </button>
  )
}

/** Warns when a "provider:model" value names a provider we don't support. */
function unknownProviderHint(value: string, validNames: string[]): string | null {
  const provider = value.split(':')[0]?.trim()
  if (!provider || !value.includes(':')) return null
  return validNames.includes(provider) ? null : `Unknown provider "${provider}"`
}

/**
 * Card section matching the AI Agents panels — rounded-sm border, plain
 * header row. The shared `Section` in ui.tsx is rounded-xl and used by other
 * pages, so this screen uses its own to match the Agents look.
 */
function ConfigSection({
  icon,
  title,
  description,
  action,
  children,
}: {
  icon: ReactNode
  title: string
  description?: string
  action?: ReactNode
  children: ReactNode
}): JSX.Element {
  return (
    <section className="rounded-sm border border-gray-200 bg-white">
      <header className="flex items-start justify-between gap-4 border-b border-gray-100 px-4 py-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-[#3454cf]/10 text-[#3454cf]">
            {icon}
          </span>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
            {description !== undefined && (
              <p className="mt-0.5 text-xs leading-relaxed text-gray-500">{description}</p>
            )}
          </div>
        </div>
        {action !== undefined && <div className="shrink-0">{action}</div>}
      </header>
      <div className="min-w-0 px-4 py-4">{children}</div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AiConfigPage(): JSX.Element {
  const toast = useToast()
  const { confirm, Dialog } = useConfirmDialog()
  const [config, setConfig] = useState<AiConfig | null>(null)
  const [health, setHealth] = useState<ProviderHealth[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshingHealth, setRefreshingHealth] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletingProvider, setDeletingProvider] = useState<string | null>(null)
  // One shared side panel for both "Add provider" and "Edit provider" — same
  // fields either way, so one form/state/save-path serves both instead of
  // duplicating the drawer. `mode` picks the copy/behavior differences:
  // 'add' always creates a new row; 'edit' patches the row named
  // `providerFormOriginalName` (null only in 'add' mode) and locks
  // name/adapter for a built-in, matching the Endpoints tab's own
  // isBuiltIn disabling.
  const [providerFormOpen, setProviderFormOpen] = useState(false)
  const [providerFormMode, setProviderFormMode] = useState<'add' | 'edit'>('add')
  const [providerFormOriginalName, setProviderFormOriginalName] = useState<string | null>(null)
  const [savingProviderForm, setSavingProviderForm] = useState(false)
  const [providerFormError, setProviderFormError] = useState<string | null>(null)
  const [providerForm, setProviderForm] = useState<CustomProvider & { apiKey: string }>({
    name: '',
    adapter: 'openai',
    baseUrl: '',
    chatModel: '',
    apiKey: '',
  })
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [testOpen, setTestOpen] = useState(false)
  const [drawerError, setDrawerError] = useState<string | null>(null)
  // Tab order follows the setup flow: authenticate -> where it lives ->
  // which model to chat with -> secondary task models -> routing policy
  // (aliases now live inside Routing, see below).
  const [drawerTab, setDrawerTab] = useState<
    'keys' | 'endpoints' | 'providers' | 'taskModels' | 'routing'
  >('keys')

  // Whether the current user can manage keys (admin:extensions:manage).
  // Key management is independent of the global Edit mode toggle — it has
  // its own save/confirm flow that persists immediately.
  const user = useAuthStore((s) => s.user)
  const canManageKeys =
    (user?.isSuperAdmin ?? false) ||
    (user?.roles?.some((r) => r === 'PLATFORM_ADMIN' || r === 'PLATFORM_SUPER_ADMIN') ?? false) ||
    (user?.permissions?.includes('*') ?? false) ||
    (user?.permissions?.includes('admin:extensions:manage') ?? false)

  // Editable form state
  const [aliases, setAliases] = useState<Record<string, string>>({})
  const [defaultProvider, setDefaultProvider] = useState('')
  const [fallbackChain, setFallbackChain] = useState<string[]>([])
  const [providerModels, setProviderModels] = useState<Record<string, string>>({})
  const [embeddingModels, setEmbeddingModels] = useState<Record<string, string>>({})
  const [codeModels, setCodeModels] = useState<Record<string, string>>({})
  const [guardrailModels, setGuardrailModels] = useState<Record<string, string>>({})
  const [providerUrls, setProviderUrls] = useState<Record<string, string>>({})
  const [customProviders, setCustomProviders] = useState<CustomProvider[]>([])

  const formError = useMemo(() => validateForm(), [providerUrls, customProviders])

  const providers = useMemo(() => sortProviders(config?.customProviders ?? []), [config])
  const providerNames = useMemo(() => providers.map((p) => p.name), [providers])
  const providerLabel = useMemo(() => {
    const byName = new Map(providers.map((p) => [p.name, p.displayName ?? p.name]))
    return (name: string): string => byName.get(name) ?? name
  }, [providers])
  const providerAdapter = useMemo(() => {
    return (name: string): CustomProvider['adapter'] | undefined =>
      providers.find((p) => p.name === name)?.adapter
  }, [providers])
  const providerOptions = useMemo(
    () => providers.map((p) => ({ label: `${p.displayName ?? p.name} · ${ADAPTER_LABELS[p.adapter]}`, value: p.name })),
    [providers],
  )

  useEffect(() => {
    void loadConfig(true)
  }, [])

  async function loadConfig(initial = false): Promise<void> {
    if (initial) setLoading(true)
    try {
      const [cfg, hlth] = await Promise.all([
        api.get<AiConfig>('/admin/ai/config'),
        api.get<{ status: string; providers: ProviderHealth[] }>('/admin/ai/health'),
      ])
      setConfig(cfg)
      setHealth(hlth.providers)
      setAliases(cfg.aliases)
      setDefaultProvider(cfg.defaultProvider)
      setFallbackChain(cfg.fallbackChain)
      setProviderModels(cfg.providerModels)
      setEmbeddingModels(cfg.embeddingModels)
      setCodeModels(cfg.codeModels ?? {})
      setGuardrailModels(cfg.guardrailModels ?? {})
      setProviderUrls(cfg.providerUrls ?? {})
      setCustomProviders(cfg.customProviders ?? [])
    } catch (err) {
      toast.error('Failed to load AI config', String(err))
    } finally {
      if (initial) setLoading(false)
    }
  }

  async function refreshHealth(): Promise<void> {
    setRefreshingHealth(true)
    try {
      const hlth = await api.get<{ status: string; providers: ProviderHealth[] }>('/admin/ai/health')
      setHealth(hlth.providers)
    } catch (err) {
      toast.error('Failed to refresh provider health', String(err))
    } finally {
      setRefreshingHealth(false)
    }
  }

  async function deleteProvider(name: string): Promise<void> {
    const target = config?.customProviders?.find((p) => p.name === name)
    if (!target || target.isBuiltIn) {
      toast.error('Cannot delete built-in provider', name)
      return
    }
    setDeletingProvider(name)
    try {
      const remaining = (config?.customProviders ?? []).filter((p) => p.name !== name)
      await api.put<AiConfig>('/admin/ai/config', { customProviders: remaining })
      toast.success('Provider deleted', name)
      await Promise.all([loadConfig(), refreshHealth()])
    } catch (err) {
      toast.error('Failed to delete provider', String(err))
    } finally {
      setDeletingProvider(null)
    }
  }

  function promptDeleteProvider(name: string): void {
    confirm({
      title: 'Delete provider?',
      message: `Delete custom provider "${providerLabel(name)}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: () => void deleteProvider(name),
    })
  }

  /** Open the shared provider panel in "add" mode, with a blank form. */
  function openAddProvider(): void {
    setProviderFormMode('add')
    setProviderFormOriginalName(null)
    setProviderForm({ name: '', adapter: 'openai', baseUrl: '', chatModel: '', apiKey: '' })
    setProviderFormError(null)
    setProviderFormOpen(true)
  }

  /** Open the shared provider panel in "edit" mode, pre-filled from that row. */
  function openEditProvider(name: string): void {
    const target = config?.customProviders?.find((p) => p.name === name)
    if (!target) return
    setProviderFormMode('edit')
    setProviderFormOriginalName(target.name)
    setProviderForm({ ...target, apiKey: '' })
    setProviderFormError(null)
    setProviderFormOpen(true)
  }

  /**
   * Save the shared provider panel — creates a new customProviders row in
   * 'add' mode, or patches the row named `providerFormOriginalName` in
   * 'edit' mode. Same validation and apiKeyRef invariant either way; only
   * the request shape (append vs. replace-in-place) and copy differ.
   */
  /**
   * Validate the current form and build the row to persist. Returns `null`
   * (after setting `providerFormError`) when validation fails, so callers
   * can bail out before ever showing a confirm dialog or hitting the API.
   */
  function buildProviderPayload(): { finalName: string; provider: CustomProvider } | null {
    const isEdit = providerFormMode === 'edit'
    const isBuiltIn = isEdit && (providerForm.isBuiltIn ?? false)
    const name = providerForm.name.trim().toLowerCase().replace(/\s+/g, '-')

    if (!isBuiltIn && !name) {
      setProviderFormError('Provider name is required')
      return null
    }
    if (
      !isBuiltIn &&
      name !== providerFormOriginalName &&
      (config?.customProviders ?? []).some((p) => p.name === name)
    ) {
      setProviderFormError(`A provider named "${name}" already exists`)
      return null
    }
    if (isEdit && !isBuiltIn && !providerForm.displayName?.trim()) {
      setProviderFormError('Display name is required')
      return null
    }
    if (providerForm.adapter !== 'ollama' && !providerForm.baseUrl.trim()) {
      setProviderFormError('Base URL is required')
      return null
    }
    if (!providerForm.chatModel.trim()) {
      setProviderFormError('Chat model is required')
      return null
    }
    if (providerForm.baseUrl.trim() && !isValidUrl(providerForm.baseUrl.trim())) {
      setProviderFormError('Base URL is not valid')
      return null
    }

    // A built-in's name never changes; a custom row may be renamed (add mode
    // always assigns the freshly-typed name). Either way apiKeyRef stays
    // self-referential — enforced server-side in the PUT handler — so
    // buildRegistry can actually resolve a key saved for this row; without
    // it a key saved moments later would never be looked up and the
    // provider would silently report "no API key" forever.
    const finalName = isBuiltIn ? (providerFormOriginalName as string) : name
    const provider: CustomProvider = {
      ...providerForm,
      name: finalName,
      baseUrl: providerForm.baseUrl.trim(),
      chatModel: providerForm.chatModel.trim(),
      embeddingModel: providerForm.embeddingModel?.trim() || undefined,
      codeModel: providerForm.codeModel?.trim() || undefined,
      guardrailModel: providerForm.guardrailModel?.trim() || undefined,
      ...(providerForm.adapter !== 'ollama' && !isBuiltIn ? { apiKeyRef: finalName } : {}),
    }
    setProviderFormError(null)
    return { finalName, provider }
  }

  /** Actually persist the validated payload — the part that follows the
   *  edit-mode confirm dialog (or runs immediately for a new provider). */
  async function submitProviderPayload(finalName: string, provider: CustomProvider): Promise<boolean> {
    const isEdit = providerFormMode === 'edit'
    setSavingProviderForm(true)
    try {
      const nextCustomProviders = isEdit
        ? (config?.customProviders ?? []).map((p) => (p.name === providerFormOriginalName ? provider : p))
        : [...(config?.customProviders ?? []), provider]
      await api.put<AiConfig>('/admin/ai/config', { customProviders: nextCustomProviders })
      if (!isEdit && providerForm.apiKey.trim() && providerForm.adapter !== 'ollama') {
        await api.put(`/admin/ai/providers/${encodeURIComponent(finalName)}/key`, {
          apiKey: providerForm.apiKey.trim(),
        })
      }
      toast.success(isEdit ? 'Provider updated' : 'Provider added', finalName)
      setProviderFormOpen(false)
      await Promise.all([loadConfig(), refreshHealth()])
      return true
    } catch (err) {
      const message = String(err)
      setProviderFormError(message)
      toast.error(isEdit ? 'Failed to update provider' : 'Failed to add provider', message)
      return false
    } finally {
      setSavingProviderForm(false)
    }
  }

  /**
   * Save the shared provider panel. Validates first (surfacing an inline
   * error and never prompting on a form that wouldn't have saved anyway);
   * editing an existing provider — including Ollama — then asks for
   * confirmation before the PUT fires, since it changes a config live
   * requests are already routing through. Adding a brand-new provider has
   * no existing behavior to disrupt, so it saves immediately.
   */
  function handleSaveProvider(): Promise<boolean> {
    const payload = buildProviderPayload()
    if (!payload) return Promise.resolve(false)
    const { finalName, provider } = payload

    if (providerFormMode === 'edit') {
      return new Promise((resolve) => {
        confirm({
          title: 'Update provider?',
          message: `Save changes to "${providerForm.displayName || finalName}"? Any request already routing to this provider will pick up the new configuration immediately.`,
          confirmLabel: 'Update',
          cancelLabel: 'Cancel',
          onConfirm: () => void submitProviderPayload(finalName, provider).then(resolve),
          onCancel: () => resolve(false),
        })
      })
    }

    return submitProviderPayload(finalName, provider)
  }

  function toggleFallbackProvider(provider: string): void {
    setFallbackChain((chain) =>
      chain.includes(provider)
        ? chain.filter((p) => p !== provider)
        : [...chain, provider],
    )
  }

  function moveFallback(provider: string, dir: -1 | 1): void {
    setFallbackChain((chain) => {
      const i = chain.indexOf(provider)
      const j = i + dir
      if (i < 0 || j < 0 || j >= chain.length) return chain
      const next = [...chain]
      ;[next[i], next[j]] = [next[j]!, next[i]!]
      return next
    })
  }

  function isValidUrl(value: string): boolean {
    if (!value) return true
    try {
      new URL(value)
      return true
    } catch {
      return false
    }
  }

  function validateForm(): string | null {
    for (const [provider, url] of Object.entries(providerUrls)) {
      if (url && !isValidUrl(url)) {
        return `${providerLabel(provider)} endpoint is not a valid URL`
      }
    }
    for (const [i, cp] of customProviders.entries()) {
      if (!cp.name.trim()) return `Custom provider #${i + 1} name is required`
      if (!cp.isBuiltIn && !cp.displayName?.trim()) return `Custom provider #${i + 1} display name is required`
      if (!cp.baseUrl.trim()) return `Custom provider #${i + 1} base URL is required`
      if (!isValidUrl(cp.baseUrl)) return `Custom provider #${i + 1} base URL is not valid`
      if (!cp.chatModel.trim()) return `Custom provider #${i + 1} chat model is required`
    }
    return null
  }

  async function handleSave(): Promise<void> {
    if (formError) {
      setDrawerError(formError)
      toast.error('Validation failed', formError)
      return
    }
    const validCustomProviders = customProviders
      .filter((cp) => cp.name.trim() && cp.baseUrl.trim() && cp.chatModel.trim())
      // Self-referential apiKeyRef is required (and enforced server-side) for
      // any key-managed row before a key saved via ProviderKeyManager can
      // ever be looked up by buildRegistry — backfill it here so every save
      // path (this drawer's inline "+ Add provider" row included) satisfies
      // the invariant, not just the dedicated Add-provider dialog.
      .map((cp) =>
        cp.adapter !== 'ollama' && !cp.isBuiltIn && !cp.apiKeyRef
          ? { ...cp, apiKeyRef: cp.name }
          : cp,
      )
    setSaving(true)
    setDrawerError(null)
    try {
      const updated = await api.put<AiConfig>('/admin/ai/config', {
        aliases,
        defaultProvider,
        fallbackChain,
        providerModels,
        embeddingModels,
        codeModels,
        guardrailModels,
        providerUrls,
        customProviders: validCustomProviders,
      })
      setConfig(updated)
      setDrawerOpen(false)
      toast.success('AI configuration saved', 'Model routing updated successfully')
      void loadConfig()
    } catch (err) {
      const message = String(err)
      setDrawerError(message)
      toast.error('Failed to save', message)
    } finally {
      setSaving(false)
    }
  }

  function hasKey(provider: string): boolean | undefined {
    const entry = providers.find((p) => p.name === provider)
    if (entry?.adapter === 'ollama') return true
    return config?.providerKeys[provider]?.hasKey
  }

  function healthStatus(provider: string): ProviderHealth | undefined {
    return health.find((h) => h.provider === provider)
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    )
  }

  const onlineCount = providerNames.filter((p) => healthStatus(p)?.available).length

  // Alias list comes from the API (aliasMeta); fall back to the config's
  // own keys (or the static label map) for older API responses.
  const aliasList: Array<{ key: string; label: string }> =
    config?.aliasMeta ??
    Object.keys(config?.aliases ?? ALIAS_LABELS).map((key) => ({
      key,
      label: ALIAS_LABELS[key] ?? key,
    }))

  return (
    <RequirePermission permission="admin:extensions:view">
      <div className="space-y-5">
        <Dialog />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-sm font-semibold text-gray-900">AI Configuration</h1>
            <p className="text-xs text-gray-500">Manage providers, model aliases, and routing preferences</p>
          </div>
          <div className="flex items-center gap-2">
            <RequirePermission permission="admin:extensions:manage" fallback={null}>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                onClick={() => setTestOpen(true)}
              >
                <FlaskConical className="h-3.5 w-3.5" />
                Test
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0]"
                onClick={() => setDrawerOpen(true)}
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit Configuration
              </button>
            </RequirePermission>
          </div>
        </div>

        {/* Provider health */}
        <ConfigSection
          icon={<Server className="h-4 w-4" />}
          title="Provider health"
          description={`${onlineCount} of ${providerNames.length} providers responding. Checked live against each endpoint.`}
          action={
            <div className="flex items-center gap-2">
              {canManageKeys && (
                <ActionButton
                  tone="primary"
                  icon={<Plus className="h-4 w-4" />}
                  label="Add provider"
                  onClick={openAddProvider}
                />
              )}
              <ActionButton
                tone="ghost"
                icon={<RefreshCw className="h-4 w-4" />}
                label="Refresh"
                busy={refreshingHealth}
                onClick={() => void refreshHealth()}
              />
            </div>
          }
        >
          <div className="grid grid-cols-1 gap-3 min-w-0 sm:grid-cols-2 lg:grid-cols-5">
            {providers.map((p) => {
              const provider = p.name
              const hlth = healthStatus(provider)
              const keyMissing = hasKey(provider) === false
              // "No key" is a configuration state, not an outage — it must not
              // read as red, or every unused cloud provider looks broken.
              const dot = hlth?.available ? 'bg-emerald-500' : keyMissing ? 'bg-gray-300' : 'bg-red-500'
              const statusLabel = hlth?.available ? 'Online' : keyMissing ? 'No API key' : 'Unreachable'
              const isOllama = p.adapter === 'ollama'
              const keyInfo = config?.providerKeys[provider] ?? null
              return (
                <div
                  key={provider}
                  className="min-w-0 rounded-sm border border-gray-200 bg-white p-3.5 transition-all duration-200 hover:border-gray-300"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[13px] font-semibold text-gray-900">{providerLabel(provider)}</span>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {canManageKeys && (
                        <button
                          type="button"
                          onClick={() => openEditProvider(provider)}
                          className="text-gray-400 transition-colors hover:text-[#3454cf]"
                          title={`Edit ${providerLabel(provider)}`}
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      )}
                      <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden />
                    </div>
                  </div>
                  {/* The endpoint, not just the provider name: with a custom
                      base URL, "OpenAI" can be a self-hosted box, and a red
                      tile is unactionable without knowing which host failed. */}
                  <p className="mt-0.5 truncate font-mono text-[10px] text-gray-400" title={hlth?.endpoint ?? ADAPTER_LABELS[providerAdapter(provider) ?? 'openai']}>
                    {hlth?.endpoint ?? ADAPTER_LABELS[providerAdapter(provider) ?? 'openai']}
                  </p>
                  <p
                    className={`mt-2 text-[11px] font-medium ${
                      hlth?.available ? 'text-emerald-600' : keyMissing ? 'text-gray-500' : 'text-red-600'
                    }`}
                  >
                    {statusLabel}
                    {hlth?.latencyMs !== undefined && hlth.available && (
                      <span className="ml-1 font-normal text-gray-400">· {hlth.latencyMs}ms</span>
                    )}
                  </p>
                  {hlth?.error && !keyMissing && (
                    <p className="mt-1 truncate text-[11px] text-gray-400" title={hlth.error}>
                      {hlth.error}
                    </p>
                  )}
                  {/* Key management — inline on the health tile.
                      Ollama is platform-level (endpoint URL, no per-tenant key);
                      the other providers get add/replace/remove actions. */}
                  {isOllama ? (
                    <div className="mt-2 text-[11px] text-gray-400">
                      Platform endpoint — configured by operator
                    </div>
                  ) : (
                    <div className="mt-2">
                      <ProviderKeyManager
                        provider={provider}
                        providerLabel={providerLabel(provider)}
                        keyInfo={keyInfo}
                        canManage={canManageKeys}
                        onChanged={() => void loadConfig()}
                      />
                      {/* Deleting the whole provider is a distinct, more
                          destructive action than removing its API key
                          (ProviderKeyManager's own "Remove" button, directly
                          above) — a solid-fill button with an explicit
                          "provider" label keeps an admin from mistaking one
                          for the other, and the top border visually separates
                          it from the key-management controls it sits below. */}
                      {!p.isBuiltIn && canManageKeys && (
                        <div className="mt-2 border-t border-gray-100 pt-2">
                          <button
                            type="button"
                            onClick={() => promptDeleteProvider(provider)}
                            disabled={deletingProvider === provider}
                            className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2 py-1 text-[10px] font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                            title={`Delete provider "${providerLabel(provider)}" — this cannot be undone`}
                          >
                            <Trash2 className="h-3 w-3" />
                            {deletingProvider === provider ? 'Deleting provider...' : 'Delete provider'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </ConfigSection>

        {/* Provider models */}
        <ConfigSection
          icon={<Cpu className="h-4 w-4" />}
          title="Provider models"
          description="The default chat model used when a request routes to each provider."
        >
          <div className="grid grid-cols-1 gap-4 min-w-0 md:grid-cols-2">
            {providerNames.map((provider) => (
              <Field
                key={provider}
                label={providerLabel(provider)}
                badge={<OverrideTag overridden={config?.overrides.providerModels[provider] !== undefined} />}
                hint={
                  hasKey(provider) === false ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium leading-4 text-amber-700">
                      <CircleAlert className="h-3 w-3" />
                      no API key
                    </span>
                  ) : undefined
                }
              >
                <ValueBox value={config?.providerModels[provider]} />
              </Field>
            ))}
          </div>
        </ConfigSection>

        {/* Task models — embedding / code / guardrail models, grouped
            since they follow the same "task -> provider:model" shape. */}
        <ConfigSection
          icon={<Boxes className="h-4 w-4" />}
          title="Task models"
          description="Per-provider models used for secondary tasks: embeddings, code generation, and guardrail classification."
        >
          <div className="flex flex-col gap-6">
            <div>
              <h3 className={DRAWER_SUBSECTION_CLASS}>Embeddings</h3>
              <p className="mb-3 text-[11px] text-gray-500">
                Used to build vector embeddings for knowledge retrieval and similarity search.
              </p>
              <div className="grid grid-cols-1 gap-4 min-w-0 md:grid-cols-2">
                {providerNames.map((provider) => (
                  <Field
                    key={provider}
                    label={providerLabel(provider)}
                    badge={<OverrideTag overridden={config?.overrides?.embeddingModels?.[provider] !== undefined} />}
                  >
                    <ValueBox value={config?.embeddingModels?.[provider]} />
                  </Field>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-5">
              <h3 className={DRAWER_SUBSECTION_CLASS}>Code</h3>
              <p className="mb-3 text-[11px] text-gray-500">
                Used for the 'code' task (code completion, generation, refactoring) and for Generate
                code on custom AI tools — a code-specialised model gives noticeably better results.
              </p>
              <div className="grid grid-cols-1 gap-4 min-w-0 md:grid-cols-2">
                {providerNames.map((provider) => (
                  <Field
                    key={provider}
                    label={providerLabel(provider)}
                    badge={<OverrideTag overridden={config?.overrides?.codeModels?.[provider] !== undefined} />}
                  >
                    <ValueBox value={config?.codeModels?.[provider]} />
                  </Field>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-5">
              <h3 className={DRAWER_SUBSECTION_CLASS}>Guardrail</h3>
              <p className="mb-3 text-[11px] text-gray-500">
                Used for the 'guardrail' task (safety / policy classification).
              </p>
              <div className="grid grid-cols-1 gap-4 min-w-0 md:grid-cols-2">
                {providerNames.map((provider) => (
                  <Field
                    key={provider}
                    label={providerLabel(provider)}
                    badge={<OverrideTag overridden={config?.overrides?.guardrailModels?.[provider] !== undefined} />}
                  >
                    <ValueBox value={config?.guardrailModels?.[provider]} />
                  </Field>
                ))}
              </div>
            </div>
          </div>
        </ConfigSection>

        {/* Routing — now owns Aliases too, since an alias is just a named
            routing preset. */}
        <ConfigSection
          icon={<Route className="h-4 w-4" />}
          title="Routing preferences"
          description="Named aliases, where a request goes first, and where it goes when that provider fails."
        >
          <div>
            <h3 className={DRAWER_SUBSECTION_CLASS}>Model aliases</h3>
            <p className="mb-3 text-[11px] text-gray-500">
              Aliases map a capability level to a provider:model pair. Workloads request an alias,
              never a raw model name.
            </p>
            <div className="grid grid-cols-1 gap-4 min-w-0 md:grid-cols-2">
              {aliasList.map(({ key, label }) => (
                <Field
                  key={key}
                  label={label}
                  badge={<OverrideTag overridden={config?.overrides.aliases[key] !== undefined} />}
                >
                  <ValueBox value={config?.aliases[key]} />
                </Field>
              ))}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 border-t border-gray-100 pt-5 min-w-0 md:grid-cols-2">
            <Field
              label="Default provider"
              badge={<OverrideTag overridden={config?.overrides.defaultProvider !== undefined} />}
            >
              <p className="-mt-1 mb-0.5 text-xs text-gray-500">Used when a workload specifies no alias</p>
              <ValueBox value={config?.defaultProvider} />
            </Field>

            <Field
              label="Fallback chain"
              badge={<OverrideTag overridden={config?.overrides.fallbackChain !== undefined} />}
            >
              <p className="-mt-1 mb-0.5 text-xs text-gray-500">Tried in order when the primary provider fails</p>
              <div className="flex flex-wrap items-center gap-1.5">
                {(config?.fallbackChain ?? []).map((p, i) => (
                  <span key={p} className="inline-flex items-center gap-1.5">
                    {i > 0 && <span className="text-gray-300">→</span>}
                    <span className="break-all rounded-sm border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-700">
                      {p}
                    </span>
                  </span>
                ))}
                {(config?.fallbackChain ?? []).length === 0 && <span className="text-sm text-gray-400">—</span>}
              </div>
            </Field>
          </div>

          {config?.ollamaUrl && (
            <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
              <span className="text-xs font-medium text-gray-500">Ollama endpoint</span>
              <code className="break-all rounded-sm border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-700">
                {config.ollamaUrl}
              </code>
            </div>
          )}
        </ConfigSection>

        {/* Test side panel — sends one prompt through the routing above.
            The full version with history lives on the Playground. */}
        <ResourceDrawer
          open={testOpen}
          onClose={() => setTestOpen(false)}
          title="Test Configuration"
          icon={<FlaskConical className="h-4 w-4 text-white" />}
          width="wide"
        >
          <div className="flex flex-col gap-3">
            <p className="text-[11px] leading-relaxed text-gray-500">
              Sends one prompt through the routing above — same resolution and fallback chain the
              runtime uses. This is a real, billable model call.{' '}
              <Link
                to="/admin/ai-testing/playground"
                className="font-medium text-[#3454cf] hover:underline"
              >
                Open Playground →
              </Link>
            </p>
            <AiTestConsole stacked />
          </div>
        </ResourceDrawer>

        {/* Provider panel — one shared side panel for both "Add provider"
            and "Edit provider", since they're the same field set either way.
            `providerFormMode` picks the copy and save behavior; a built-in
            row (edit mode only) keeps name/adapter locked, matching the
            Endpoints tab's own isBuiltIn disabling. Reachable from the
            Provider health section's "Add provider" button and from each
            tile's Edit button (every provider except Ollama). */}
        <ResourceDrawer
          open={providerFormOpen}
          onClose={() => setProviderFormOpen(false)}
          onSave={() => handleSaveProvider().then(() => {})}
          title={
            providerFormMode === 'edit'
              ? `Edit ${providerForm.displayName || providerForm.name || 'provider'}`
              : 'Add provider'
          }
          icon={
            providerFormMode === 'edit' ? (
              <Pencil className="h-4 w-4 text-white" />
            ) : (
              <Plus className="h-4 w-4 text-white" />
            )
          }
          width="standard"
          saving={savingProviderForm}
          saveLabel={providerFormMode === 'edit' ? 'Save changes' : 'Add provider'}
        >
          <div className="flex flex-col gap-4">
            {providerFormError && (
              <div className="-mx-6 -mt-6 mb-2 border-b border-red-100 bg-red-50 px-6 py-3 text-xs text-red-700">
                {providerFormError}
              </div>
            )}
            <div>
              <label className={DRAWER_LABEL_CLASS}>Display name</label>
              <InputText
                value={providerForm.displayName ?? ''}
                onChange={(e) => setProviderForm({ ...providerForm, displayName: e.target.value })}
                placeholder="e.g. My vLLM Server"
                className={DRAWER_INPUT_CLASS}
                disabled={providerForm.isBuiltIn}
              />
            </div>
            <div>
              <label className={DRAWER_LABEL_CLASS}>Name (routing key)</label>
              <InputText
                value={providerForm.name}
                onChange={(e) => setProviderForm({ ...providerForm, name: e.target.value })}
                placeholder="e.g. my-vllm"
                className={DRAWER_INPUT_CLASS}
                disabled={providerForm.isBuiltIn}
              />
              <span className="mt-1 block text-[10px] text-gray-400">
                Unique key used in aliases and routing (lowercase, no spaces)
              </span>
            </div>
            <div>
              <label className={DRAWER_LABEL_CLASS}>Adapter</label>
                            <Select
                ariaLabel={"Adapter"}
                size="sm"
                value={providerForm.adapter}
                onChange={(v) => setProviderForm({ ...providerForm, adapter: v as CustomProvider['adapter'] })}
                disabled={providerForm.isBuiltIn ?? false}
              >
                <option value="openai">OpenAI-compatible</option>
                <option value="ollama">Ollama native</option>
                <option value="groq">Groq</option>
                <option value="anthropic">Anthropic</option>
                <option value="google">Google</option>
                            </Select>
              <span className="mt-1 block text-[10px] text-gray-400">
                Determines which SDK the runtime uses to call the base URL
              </span>
            </div>
            <div>
              <label className={DRAWER_LABEL_CLASS}>Base URL</label>
              <InputText
                value={providerForm.baseUrl}
                onChange={(e) => setProviderForm({ ...providerForm, baseUrl: e.target.value })}
                placeholder="http://host:port"
                className={DRAWER_INPUT_CLASS}
              />
            </div>
            <div>
              <label className={DRAWER_LABEL_CLASS}>Chat model</label>
              <InputText
                value={providerForm.chatModel}
                onChange={(e) => setProviderForm({ ...providerForm, chatModel: e.target.value })}
                placeholder="model:tag"
                className={DRAWER_INPUT_CLASS}
              />
            </div>
            <div>
              <label className={DRAWER_LABEL_CLASS}>Embedding model (optional)</label>
              <InputText
                value={providerForm.embeddingModel ?? ''}
                onChange={(e) =>
                  setProviderForm({ ...providerForm, embeddingModel: e.target.value || undefined })
                }
                placeholder="defaults to chat model"
                className={DRAWER_INPUT_CLASS}
              />
            </div>
            <div>
              <label className={DRAWER_LABEL_CLASS}>Code model (optional)</label>
              <InputText
                value={providerForm.codeModel ?? ''}
                onChange={(e) => setProviderForm({ ...providerForm, codeModel: e.target.value || undefined })}
                placeholder="defaults to chat model"
                className={DRAWER_INPUT_CLASS}
              />
            </div>
            <div>
              <label className={DRAWER_LABEL_CLASS}>Guardrail model (optional)</label>
              <InputText
                value={providerForm.guardrailModel ?? ''}
                onChange={(e) =>
                  setProviderForm({ ...providerForm, guardrailModel: e.target.value || undefined })
                }
                placeholder="defaults to chat model"
                className={DRAWER_INPUT_CLASS}
              />
            </div>
            {providerForm.adapter !== 'ollama' && providerFormMode === 'edit' && providerFormOriginalName && (
              <div>
                <label className={DRAWER_LABEL_CLASS}>API key</label>
                <ProviderKeyManager
                  provider={providerFormOriginalName}
                  providerLabel={providerForm.displayName || providerForm.name}
                  keyInfo={config?.providerKeys[providerFormOriginalName] ?? null}
                  canManage={canManageKeys}
                  onChanged={() => void loadConfig()}
                />
              </div>
            )}
            {providerForm.adapter !== 'ollama' && providerFormMode === 'add' && (
              <div>
                <label className={DRAWER_LABEL_CLASS}>API key</label>
                <InputText
                  type="password"
                  value={providerForm.apiKey}
                  onChange={(e) => setProviderForm({ ...providerForm, apiKey: e.target.value })}
                  placeholder="sk-..."
                  className={DRAWER_INPUT_CLASS}
                />
                <span className="mt-1 block text-[10px] text-gray-400">
                  Stored encrypted; optional, but a key-managed provider will be unreachable without one
                </span>
              </div>
            )}
          </div>
        </ResourceDrawer>

        {/* Edit drawer — all mutable fields live here, same pattern as the
            other AI Center panels (Safety, Guardrails, Agents). */}
        <ResourceDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onSave={handleSave}
          title="AI Configuration"
          icon={<Cpu className="h-4 w-4 text-white" />}
          width="xl"
          saving={saving}
          saveLabel="Save Changes"
        >
          {(drawerError ?? formError) && (
            <div className="-mx-6 -mt-6 mb-5 border-b border-red-100 bg-red-50 px-6 py-3 text-xs text-red-700">
              {drawerError ?? formError}
            </div>
          )}
          {/* Tab bar — same style as the tool/agent drawers. Order mirrors
              the setup flow: authenticate -> where it lives -> chat model ->
              secondary task models -> routing policy (aliases live inside
              Routing). */}
          <div className="-mx-6 -mt-6 mb-5 flex border-b border-gray-200 px-4">
            {(
              [
                { key: 'keys' as const, label: 'API Keys', icon: <Server className="h-3.5 w-3.5" /> },
                { key: 'endpoints' as const, label: 'Endpoints', icon: <Server className="h-3.5 w-3.5" /> },
                { key: 'providers' as const, label: 'Models', icon: <Cpu className="h-3.5 w-3.5" /> },
                { key: 'taskModels' as const, label: 'Task Models', icon: <Boxes className="h-3.5 w-3.5" /> },
                { key: 'routing' as const, label: 'Routing', icon: <Route className="h-3.5 w-3.5" /> },
              ] as const
            ).map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setDrawerTab(tab.key)}
                className={`flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors ${
                  drawerTab === tab.key
                    ? 'border-[#3454cf] text-[#3454cf]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-6">
            {/* API keys — add/replace per provider; each action opens its own
                key drawer (ProviderKeyManager handles validate/save/remove). */}
            {drawerTab === 'keys' && (
            <div className="flex flex-col gap-3">
              <div>
                <h4 className={DRAWER_SECTION_CLASS}>API keys</h4>
                <p className="mt-0.5 text-[11px] text-gray-400">
                  Per-tenant provider credentials — saved immediately, independent of this form.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {providers.filter((p) => p.adapter !== 'ollama').map((p) => (
                  <div
                    key={p.name}
                    className="rounded-sm border border-gray-200 bg-gray-50/60 px-3 py-2.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-gray-800">
                        {providerLabel(p.name)}
                      </span>
                      {hasKey(p.name) === false && (
                        <span className="inline-flex items-center gap-1 rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium leading-4 text-amber-700">
                          <CircleAlert className="h-3 w-3" />
                          no API key
                        </span>
                      )}
                    </div>
                    <ProviderKeyManager
                      provider={p.name}
                      providerLabel={providerLabel(p.name)}
                      keyInfo={config?.providerKeys[p.name] ?? null}
                      canManage={canManageKeys}
                      onChanged={() => void loadConfig()}
                    />
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Endpoints / base URLs */}
            {drawerTab === 'endpoints' && (
            <div className="flex flex-col gap-4">
              <div>
                <h4 className={DRAWER_SECTION_CLASS}>Provider endpoints</h4>
                <p className="mt-0.5 text-[11px] text-gray-400">
                  Base URLs for each provider. Empty = inherit from platform env. Cloud providers
                  (OpenAI, Groq, Anthropic, Google) rarely need this — it mainly matters for
                  self-hosted Ollama or an OpenAI-compatible proxy.
                </p>
              </div>
              <div className="mt-2">
                <div className="mb-2 flex items-center justify-between">
                  <h4 className={DRAWER_SECTION_CLASS}>Providers</h4>
                  <button
                    type="button"
                    onClick={() =>
                      setCustomProviders([
                        ...customProviders,
                        { name: '', adapter: 'openai', baseUrl: '', chatModel: '', displayName: '' },
                      ])
                    }
                    className="text-[10px] font-medium text-[#3454cf] hover:text-[#2a45b0]"
                  >
                    + Add provider
                  </button>
                </div>
                {customProviders.map((cp, idx) => (
                  <div
                    key={idx}
                    className="mb-3 grid grid-cols-1 gap-3 rounded-sm border border-gray-200 bg-gray-50/60 p-3 md:grid-cols-2"
                  >
                    <div>
                      <label className={DRAWER_LABEL_CLASS}>Display name</label>
                      <InputText
                        value={cp.displayName ?? ''}
                        onChange={(e) => {
                          const next = [...customProviders]
                          next[idx] = { ...cp, displayName: e.target.value }
                          setCustomProviders(next)
                        }}
                        placeholder="e.g. My vLLM Server"
                        className={DRAWER_INPUT_CLASS}
                        disabled={cp.isBuiltIn}
                      />
                    </div>
                    <div>
                      <label className={DRAWER_LABEL_CLASS}>Name (routing key)</label>
                      <InputText
                        value={cp.name}
                        onChange={(e) => {
                          const next = [...customProviders]
                          next[idx] = { ...cp, name: e.target.value }
                          setCustomProviders(next)
                        }}
                        placeholder="e.g. my-vllm"
                        className={DRAWER_INPUT_CLASS}
                        disabled={cp.isBuiltIn}
                      />
                    </div>
                    <div>
                      <label className={DRAWER_LABEL_CLASS}>Adapter</label>
                                            <Select
                        ariaLabel={"Adapter"}
                        size="sm"
                        value={cp.adapter}
                        onChange={(v) => {
                          const next = [...customProviders]
                          next[idx] = { ...cp, adapter: v as CustomProvider['adapter'] }
                          setCustomProviders(next)
                        }}
                        disabled={cp.isBuiltIn ?? false}
                      >
                        <option value="openai">OpenAI-compatible</option>
                        <option value="ollama">Ollama native</option>
                        <option value="anthropic">Anthropic</option>
                        <option value="google">Google</option>
                        <option value="groq">Groq</option>
                                            </Select>
                    </div>
                    <div>
                      <label className={DRAWER_LABEL_CLASS}>Base URL</label>
                      <InputText
                        value={cp.baseUrl}
                        onChange={(e) => {
                          const next = [...customProviders]
                          next[idx] = { ...cp, baseUrl: e.target.value }
                          setCustomProviders(next)
                        }}
                        placeholder="http://host:port"
                        className={DRAWER_INPUT_CLASS}
                      />
                    </div>
                    <div>
                      <label className={DRAWER_LABEL_CLASS}>Chat model</label>
                      <InputText
                        value={cp.chatModel}
                        onChange={(e) => {
                          const next = [...customProviders]
                          next[idx] = { ...cp, chatModel: e.target.value }
                          setCustomProviders(next)
                        }}
                        placeholder="model:tag"
                        className={DRAWER_INPUT_CLASS}
                      />
                    </div>
                    <div>
                      <label className={DRAWER_LABEL_CLASS}>Embedding model (optional)</label>
                      <InputText
                        value={cp.embeddingModel ?? ''}
                        onChange={(e) => {
                          const next = [...customProviders]
                          next[idx] = { ...cp, embeddingModel: e.target.value || undefined }
                          setCustomProviders(next)
                        }}
                        placeholder="defaults to chat model"
                        className={DRAWER_INPUT_CLASS}
                      />
                    </div>
                    <div>
                      <label className={DRAWER_LABEL_CLASS}>Code model (optional)</label>
                      <InputText
                        value={cp.codeModel ?? ''}
                        onChange={(e) => {
                          const next = [...customProviders]
                          next[idx] = { ...cp, codeModel: e.target.value || undefined }
                          setCustomProviders(next)
                        }}
                        placeholder="defaults to chat model"
                        className={DRAWER_INPUT_CLASS}
                      />
                    </div>
                    <div>
                      <label className={DRAWER_LABEL_CLASS}>Guardrail model (optional)</label>
                      <InputText
                        value={cp.guardrailModel ?? ''}
                        onChange={(e) => {
                          const next = [...customProviders]
                          next[idx] = { ...cp, guardrailModel: e.target.value || undefined }
                          setCustomProviders(next)
                        }}
                        placeholder="defaults to chat model"
                        className={DRAWER_INPUT_CLASS}
                      />
                    </div>
                    {cp.adapter !== 'ollama' && !cp.isBuiltIn && (
                      <div className="md:col-span-2">
                        <ProviderKeyManager
                          provider={cp.name}
                          providerLabel={cp.displayName || cp.name}
                          keyInfo={config?.providerKeys[cp.name] ?? null}
                          canManage={canManageKeys}
                          onChanged={() => void loadConfig()}
                        />
                      </div>
                    )}
                    {!cp.isBuiltIn && (
                      <div className="md:col-span-2 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            setCustomProviders(customProviders.filter((_, i) => i !== idx))
                          }
                          className="text-[10px] font-medium text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {customProviders.length === 0 && (
                  <p className="text-[11px] text-gray-400">No providers configured.</p>
                )}
              </div>
            </div>
            )}

            {/* Provider models */}
            {drawerTab === 'providers' && (
            <div className="flex flex-col gap-4">
              <div>
                <h4 className={DRAWER_SECTION_CLASS}>Provider models</h4>
                <p className="mt-0.5 text-[11px] text-gray-400">
                  The default chat model used when a request routes to each provider.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {providerNames.map((provider) => (
                  <div key={provider}>
                    <label className={`${DRAWER_LABEL_CLASS} flex items-center gap-2`}>
                      {providerLabel(provider)}
                      <ResetLink
                        platformValue={config?.platform?.providerModels[provider]}
                        currentValue={providerModels[provider] ?? ''}
                        onReset={() =>
                          setProviderModels({
                            ...providerModels,
                            [provider]: config?.platform?.providerModels[provider] ?? '',
                          })
                        }
                      />
                      {hasKey(provider) === false && (
                        <span className="inline-flex items-center gap-1 rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium leading-4 text-amber-700">
                          <CircleAlert className="h-3 w-3" />
                          no API key
                        </span>
                      )}
                    </label>
                    <InputText
                      value={providerModels[provider] ?? ''}
                      onChange={(e) =>
                        setProviderModels({ ...providerModels, [provider]: e.target.value })
                      }
                      placeholder={MODEL_SUGGESTIONS[provider]?.[0] ?? 'model name'}
                      list={`chat-models-${provider}`}
                      className={DRAWER_INPUT_CLASS}
                    />
                    <datalist id={`chat-models-${provider}`}>
                      {(MODEL_SUGGESTIONS[provider] ?? []).map((m) => (
                        <option key={m} value={m} />
                      ))}
                    </datalist>
                    <span className="mt-1 block text-[10px] text-gray-400">
                      e.g. {MODEL_SUGGESTIONS[provider]?.[0]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Task models — Embeddings + Code + Guardrail merged into one
                tab, since each is the same "task -> provider:model" shape. */}
            {drawerTab === 'taskModels' && (
            <div className="flex flex-col gap-6">
              <div>
                <h4 className={DRAWER_SECTION_CLASS}>Task models</h4>
                <p className="mt-0.5 text-[11px] text-gray-400">
                  Per-provider models for secondary tasks. Chat uses the Models tab; these apply to
                  embeddings, code generation, and guardrail classification only.
                </p>
              </div>

              {/* Embeddings */}
              <div>
                <h5 className={DRAWER_SUBSECTION_CLASS}>Embeddings</h5>
                <p className="mb-3 text-[11px] text-gray-400">
                  Used to build vector embeddings for knowledge retrieval and similarity search.
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {providerNames.map((provider) => (
                    <div key={provider}>
                      <label className={`${DRAWER_LABEL_CLASS} flex items-center`}>
                        {providerLabel(provider)}
                        <ResetLink
                          platformValue={config?.platform?.embeddingModels[provider]}
                          currentValue={embeddingModels[provider] ?? ''}
                          onReset={() =>
                            setEmbeddingModels({
                              ...embeddingModels,
                              [provider]: config?.platform?.embeddingModels[provider] ?? '',
                            })
                          }
                        />
                      </label>
                      <InputText
                        value={embeddingModels[provider] ?? ''}
                        onChange={(e) =>
                          setEmbeddingModels({ ...embeddingModels, [provider]: e.target.value })
                        }
                        placeholder={EMBEDDING_SUGGESTIONS[provider]?.[0] ?? 'embedding model name'}
                        list={`embed-models-${provider}`}
                        className={DRAWER_INPUT_CLASS}
                      />
                      <datalist id={`embed-models-${provider}`}>
                        {(EMBEDDING_SUGGESTIONS[provider] ?? []).map((m) => (
                          <option key={m} value={m} />
                        ))}
                      </datalist>
                      <span className="mt-1 block text-[10px] text-gray-400">
                        e.g. {EMBEDDING_SUGGESTIONS[provider]?.[0]} — used for knowledge search, not chat
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Code */}
              <div className="border-t border-gray-100 pt-5">
                <h5 className={DRAWER_SUBSECTION_CLASS}>Code</h5>
                <p className="mb-3 text-[11px] text-gray-400">
                  Used for the 'code' task (code completion, generation, refactoring). Also powers
                  Generate code for custom AI tools — a code-specialised model (e.g. qwen2.5-coder)
                  gives noticeably better results.
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {providerNames.map((provider) => (
                    <div key={provider}>
                      <label className={`${DRAWER_LABEL_CLASS} flex items-center`}>
                        {providerLabel(provider)}
                        <ResetLink
                          platformValue={config?.platform?.codeModels?.[provider]}
                          currentValue={codeModels[provider] ?? ''}
                          onReset={() =>
                            setCodeModels({
                              ...codeModels,
                              [provider]: config?.platform?.codeModels?.[provider] ?? '',
                            })
                          }
                        />
                      </label>
                      <ModelSelect
                        value={codeModels[provider] ?? ''}
                        onChange={(model) =>
                          setCodeModels({ ...codeModels, [provider]: model })
                        }
                        options={CODE_SUGGESTIONS[provider] ?? []}
                        placeholder={CODE_SUGGESTIONS[provider]?.[0] ?? 'code model name'}
                      />
                      <span className="mt-1 block text-[10px] text-gray-400">
                        Used for code generation tasks, not chat
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Guardrail */}
              <div className="border-t border-gray-100 pt-5">
                <h5 className={DRAWER_SUBSECTION_CLASS}>Guardrail</h5>
                <p className="mb-3 text-[11px] text-gray-400">
                  Used for the 'guardrail' task (safety / policy classification).
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {providerNames.map((provider) => (
                    <div key={provider}>
                      <label className={`${DRAWER_LABEL_CLASS} flex items-center`}>
                        {providerLabel(provider)}
                        <ResetLink
                          platformValue={config?.platform?.guardrailModels?.[provider]}
                          currentValue={guardrailModels[provider] ?? ''}
                          onReset={() =>
                            setGuardrailModels({
                              ...guardrailModels,
                              [provider]: config?.platform?.guardrailModels?.[provider] ?? '',
                            })
                          }
                        />
                      </label>
                      <ModelSelect
                        value={guardrailModels[provider] ?? ''}
                        onChange={(model) =>
                          setGuardrailModels({ ...guardrailModels, [provider]: model })
                        }
                        options={GUARDRAIL_SUGGESTIONS[provider] ?? []}
                        placeholder={GUARDRAIL_SUGGESTIONS[provider]?.[0] ?? 'guardrail model name'}
                      />
                      <span className="mt-1 block text-[10px] text-gray-400">
                        Used for safety / policy classification, not chat
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            )}

            {/* Routing — now owns Aliases too, since an alias is just a
                named routing preset that references models defined above. */}
            {drawerTab === 'routing' && (
            <div className="flex flex-col gap-6">
              {/* Aliases */}
              <div>
                <h4 className={DRAWER_SECTION_CLASS}>Model aliases</h4>
                <p className="mt-0.5 mb-3 text-[11px] text-gray-400">
                  Aliases map a capability level to a provider:model pair.
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {aliasList.map(({ key, label }) => (
                    <div key={key}>
                      <label className={`${DRAWER_LABEL_CLASS} flex items-center`}>
                        {label}
                        <ResetLink
                          platformValue={config?.platform?.aliases[key]}
                          currentValue={aliases[key] ?? ''}
                          onReset={() =>
                            setAliases({
                              ...aliases,
                              [key]: config?.platform?.aliases[key] ?? '',
                            })
                          }
                        />
                      </label>
                      <InputText
                        value={aliases[key] ?? ''}
                        onChange={(e) => setAliases({ ...aliases, [key]: e.target.value })}
                        placeholder="provider:model"
                        className={DRAWER_INPUT_CLASS}
                      />
                      {(() => {
                        const hint = unknownProviderHint(aliases[key] ?? '', providerNames)
                        return hint ? (
                          <span className="mt-1 flex items-center gap-1 text-[10px] text-amber-600">
                            <CircleAlert className="h-3 w-3" />
                            {hint}
                          </span>
                        ) : null
                      })()}
                    </div>
                  ))}
                </div>
              </div>

              {/* Default provider / fallback chain */}
              <div className="border-t border-gray-100 pt-5">
                <h4 className={DRAWER_SECTION_CLASS}>Routing preferences</h4>
                <p className="mt-0.5 mb-3 text-[11px] text-gray-400">
                  Where a request goes first, and where it goes when that provider fails.
                </p>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className={DRAWER_LABEL_CLASS}>Default provider</label>
                    <p className="-mt-1 mb-0.5 text-[11px] text-gray-400">
                      Used when a workload specifies no alias
                    </p>
                    <Dropdown
                      value={defaultProvider}
                      options={providerOptions}
                      // Without optionValue, PrimeReact hands back the whole
                      // { label, value } option object, not the value.
                      optionLabel="label"
                      optionValue="value"
                      onChange={(e: { value: string }) => setDefaultProvider(e.value)}
                      className={DROPDOWN_CLASS}
                      panelClassName={DROPDOWN_PANEL_CLASS}
                    />
                  </div>

                  <div>
                    <label className={DRAWER_LABEL_CLASS}>Fallback chain</label>
                    <p className="-mt-1 mb-0.5 text-[11px] text-gray-400">
                      Check providers to include — tried top to bottom when the primary provider fails
                    </p>
                    <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                      {providerNames.map((p) => (
                        <label
                          key={p}
                          className="flex items-center gap-2 rounded-sm border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700"
                        >
                          <input
                            type="checkbox"
                            checked={fallbackChain.includes(p)}
                            onChange={() => toggleFallbackProvider(p)}
                            className="rounded border-gray-300 text-[#3454cf]"
                          />
                          {providerLabel(p)}
                        </label>
                      ))}
                    </div>
                    {fallbackChain.length > 0 && (
                      <div className="mt-2 flex flex-col gap-1">
                        {fallbackChain.map((p, i) => (
                          <div
                            key={p}
                            className="flex items-center gap-2 rounded-sm border border-gray-200 bg-gray-50/60 px-2.5 py-1.5"
                          >
                            <span className="w-4 text-center font-mono text-[10px] text-gray-400">
                              {i + 1}
                            </span>
                            <span className="flex-1 text-xs font-medium text-gray-800">
                              {providerLabel(p)}
                            </span>
                            <button
                              type="button"
                              onClick={() => moveFallback(p, -1)}
                              disabled={i === 0}
                              aria-label={`Move ${p} up`}
                              className="rounded-sm p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
                            >
                              <ChevronUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveFallback(p, 1)}
                              disabled={i === fallbackChain.length - 1}
                              aria-label={`Move ${p} down`}
                              className="rounded-sm p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
                            >
                              <ChevronDown className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        </ResourceDrawer>
      </div>
    </RequirePermission>
  )
}